import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight, Phone } from "lucide-react";
import { useConsultation } from "@/components/ConsultationModal";

const SERVICE_LINKS = [
  { to: "/services", label: "Services Overview" },
  { to: "/services/web-development", label: "Web Development" },
  { to: "/services/app-software", label: "App & Software" },
  { to: "/services/it-cloud-security", label: "IT, Cloud & Security" },
];

const NAV_ITEMS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { label: "Services", testid: "nav-services-link", dropdown: SERVICE_LINKS },
  { to: "/about", label: "About Us", testid: "nav-about-link" },
  { to: "/products", label: "Products", testid: "nav-products-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
      <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-electric to-cyan-400 shadow-[0_0_25px_rgba(0,240,255,0.35)]">
        <span className="font-display text-lg font-black text-ink">T</span>
      </span>
      <span className="font-display text-lg font-bold tracking-tight text-white">
        Tech<span className="text-cyan-400">Axis</span>
        <span className="ml-1 text-[10px] font-medium uppercase tracking-widest text-slate-500">Ltd.</span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open } = useConsultation();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
      isActive ? "bg-cyan-400/10 text-cyan-300" : "text-slate-300 hover:text-white"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass shadow-[0_8px_40px_rgba(3,7,18,0.6)]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex" data-testid="nav-desktop-menu">
          {NAV_ITEMS.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="group relative">
                <button
                  type="button"
                  data-testid={item.testid}
                  className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    location.pathname.startsWith("/services") ? "bg-cyan-400/10 text-cyan-300" : "text-slate-300 hover:text-white"
                  }`}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </button>
                <div className="invisible absolute left-0 top-full pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="glass w-64 rounded-xl p-2 shadow-2xl">
                    {item.dropdown.map((d) => (
                      <NavLink
                        key={d.to}
                        to={d.to}
                        data-testid={`nav-dropdown-${d.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={({ isActive }) =>
                          `block rounded-lg px-3 py-2.5 text-sm transition-colors ${
                            isActive ? "bg-cyan-400/10 text-cyan-300" : "text-slate-300 hover:bg-white/5 hover:text-white"
                          }`
                        }
                      >
                        {d.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkCls} data-testid={item.testid}>
                {item.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <button
            type="button"
            onClick={open}
            data-testid="nav-consultation-button"
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
          >
            <Phone className="h-3.5 w-3.5" />
            Free Consultation
          </button>
          <Link
            to="/quote"
            data-testid="nav-get-quote-button"
            className="group flex items-center gap-2 rounded-full bg-electric px-5 py-2 text-sm font-semibold text-white shadow-[0_0_25px_rgba(0,102,255,0.4)] transition-all hover:bg-cyan-400 hover:text-ink"
          >
            Get a Quote
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        <button
          type="button"
          className="rounded-lg p-2 text-slate-200 lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle navigation menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="glass border-t border-white/10 lg:hidden" data-testid="nav-mobile-menu">
          <div className="space-y-1 px-4 py-4">
            <NavLink to="/" end className={linkCls} data-testid="nav-mobile-home-link" style={{ display: "block" }}>
              Home
            </NavLink>
            {SERVICE_LINKS.map((d) => (
              <NavLink key={d.to} to={d.to} className={linkCls} style={{ display: "block" }} data-testid={`nav-mobile-${d.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {d.label}
              </NavLink>
            ))}
            <NavLink to="/about" className={linkCls} style={{ display: "block" }} data-testid="nav-mobile-about-link">
              About Us
            </NavLink>
            <NavLink to="/products" className={linkCls} style={{ display: "block" }} data-testid="nav-mobile-products-link">
              Products
            </NavLink>
            <NavLink to="/contact" className={linkCls} style={{ display: "block" }} data-testid="nav-mobile-contact-link">
              Contact
            </NavLink>
            <div className="flex gap-3 pt-3">
              <button
                type="button"
                onClick={() => { setMobileOpen(false); open(); }}
                data-testid="nav-mobile-consultation-button"
                className="flex-1 rounded-full border border-white/15 px-4 py-2.5 text-center text-sm font-medium text-slate-200"
              >
                Free Consultation
              </button>
              <Link to="/quote" data-testid="nav-mobile-quote-button" className="flex-1 rounded-full bg-electric px-4 py-2.5 text-center text-sm font-semibold text-white">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
